import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Show, SignUp } from "@clerk/react";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import { Toaster } from "@/components/ui/sonner";
import AppShell from "@/components/AppShell";
import Landing from "@/pages/Landing";
import Shop from "@/pages/Shop";
import QRProducts from "@/pages/QRProducts";
import About from "@/pages/About";
import AuthLayout from "@/components/site/AuthLayout";
import Home from "@/pages/Home";
import Onboarding from "@/pages/Onboarding";
import InviteAccept from "@/pages/InviteAccept";
import ProfileEditor from "@/pages/ProfileEditor";
import MyQR from "@/pages/MyQR";
import LostPoster from "@/pages/LostPoster";
import MyPage from "@/pages/MyPage";
import SignInPage, { GoRedirect } from "@/pages/SignInPage";
import OwnerOverview from "@/pages/OwnerOverview";
import Team from "@/pages/group/Team";
import MemberCard from "@/pages/group/MemberCard";
import Attendance from "@/pages/group/Attendance";
import { MfaGate } from "@/components/MfaGate";
import Outbox from "@/pages/Outbox";
import Checkout from "@/pages/Checkout";
import PrintConsole from "@/pages/PrintConsole";
import PrintOrder from "@/pages/PrintOrder";
import SuperAdmin from "@/pages/SuperAdmin";
import ReaderStation from "@/pages/ReaderStation";
import GroupLayout from "@/pages/group/GroupLayout";
import Dashboard from "@/pages/group/Dashboard";
import Tags from "@/pages/group/Tags";
import Orders from "@/pages/group/Orders";
import Scans from "@/pages/group/Scans";
import Settings from "@/pages/group/Settings";
import Nurse from "@/pages/group/Nurse";

const Protected = ({ children }) => (
  <>
    <Show when="signed-in">{children}</Show>
    <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
  </>
);

const ReaderGate = ({ children }) => {
  const [mfa, setMfa] = useState(false);
  useEffect(() => {
    const on = () => setMfa(true);
    window.addEventListener("bsafe-mfa", on);
    return () => window.removeEventListener("bsafe-mfa", on);
  }, []);
  return mfa ? <MfaGate onDone={() => window.location.reload()} /> : children;
};

const SignUpPage = () => {
  const email = new URLSearchParams(window.location.search).get("email") || undefined;
  return <SignUp routing="path" path="/sign-up" signInUrl="/sign-in" initialValues={email ? { emailAddress: email } : undefined} />;
};

function ScrollManager() {
  const { pathname, hash, search } = useLocation();
  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return undefined; }
    let tries = 0;
    const t = setInterval(() => {
      const el = document.getElementById(hash.slice(1));
      if (el || ++tries > 30) { clearInterval(t); el?.scrollIntoView({ behavior: "smooth" }); }
    }, 100);
    return () => clearInterval(t);
  }, [pathname, hash, search]);
  return null;
}

const ThemedToaster = () => {
  const { pathname } = useLocation();
  const dark = pathname.startsWith("/app") || pathname.startsWith("/reader") || pathname.startsWith("/invite");
  return <Toaster theme={dark ? "dark" : "light"} position={dark ? "top-right" : "bottom-center"} richColors />;
};

const HomeRoute = () => {
  const { hash } = useLocation();
  if (hash) return <Landing />;
  return <><Show when="signed-in"><Navigate to="/app" replace /></Show><Show when="signed-out"><Landing /></Show></>;
};

function App() {
  return (
    <BrowserRouter>
      <ClerkAuthProvider>
        <ThemedToaster />
        <ScrollManager />
        <Routes>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/sign-in/*" element={<SignInPage />} />
          <Route path="/sign-up/*" element={<AuthLayout mode="sign-up"><SignUpPage /></AuthLayout>} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/products" element={<QRProducts />} />
          <Route path="/about" element={<About />} />
          <Route path="/invite/:token" element={<InviteAccept />} />
          <Route path="/reader/:gid" element={<Protected><ReaderGate><ReaderStation /></ReaderGate></Protected>} />
          <Route path="/app" element={<Protected><AppShell /></Protected>}>
            <Route index element={<Home />} />
            <Route path="onboarding" element={<Onboarding />} />
            <Route path="profile/:pid" element={<ProfileEditor />} />
            <Route path="profile/:pid/qr" element={<MyQR />} />
            <Route path="profile/:pid/poster" element={<LostPoster />} />
            <Route path="my-page" element={<MyPage />} />
            <Route path="go" element={<GoRedirect />} />
            <Route path="owner" element={<OwnerOverview />} />
            <Route path="outbox" element={<Outbox />} />
            <Route path="checkout" element={<Checkout />} />
            <Route path="print" element={<PrintConsole />} />
            <Route path="print/:oid" element={<PrintOrder />} />
            <Route path="admin" element={<SuperAdmin />} />
            <Route path="g/:gid" element={<GroupLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="tags" element={<Tags />} />
              <Route path="orders" element={<Orders />} />
              <Route path="scans" element={<Scans />} />
              <Route path="settings" element={<Settings />} />
              <Route path="nurse" element={<Nurse />} />
              <Route path="team" element={<Team />} />
              <Route path="attendance" element={<Attendance />} />
              <Route path="members/:pid" element={<MemberCard />} />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ClerkAuthProvider>
    </BrowserRouter>
  );
}

export default App;
